import { useCallback } from "react";
import { document, sectionApi } from '~/api';
import { useRouter } from "./useRouter";
import debounce from 'lodash/debounce';
import { remoteLog } from '~/util/remoteLogger';
import isEmpty from 'lodash/isEmpty';

/**
 * Returns an `onLoad` function that can be passed to `useForm` for initializing form state
 * 
 * @param {number} sectionType The section type identifier
 * @param {number} titleOpinionId The id of the parent title opinion
 * @returns {{onLoad: (values: object) => Promise<object>}}
 */
export function useLoadSection(sectionType, titleOpinionId, additionalSectionType) {

    const load = useCallback(async (values) => {
        const data = { ...values };

        if (titleOpinionId != 0) {
            const section = await sectionApi.getSection(titleOpinionId, sectionType);
            const details = await sectionApi.getSectionDetail(titleOpinionId, sectionType);
            const additionalSection = additionalSectionType ? await sectionApi.getSection(titleOpinionId, additionalSectionType) : null;

            let documents = [];

            if (Array.isArray(section.documents)) {
                documents = section.documents
                    .map(d => {
                        return {
                            file: {
                                path: d.name
                            },
                            type: d.typeId,
                            uploaded: true,
                            isDeleted: false,
                            ...d
                        };
                    });
            }

            return {
                ...data,
                sectionId: section.sectionId,
                ...section._data,
                sectionDetails: details,
                additionalSection: additionalSectionType ? additionalSection._data : null,
                documents
            };
        }
        return data;
    }, [sectionType, titleOpinionId]);

    return {
        onLoad: load
    };
}

/**
 * 
 * @typedef AutoSaver
 * @property {(value: any, path: string, id: number?, stateValues: object) => Promise<object>} saveField
 * @property {(doc: object, docType: number, id: number) => Promise<object>} saveDocument
 * @property {(docId: number, id: number) => Promise<void>} deleteDocument
 * Returns a debounced function for persisting form data in real time
 * 
 * @param {number} sectionType The section type identifier
 * @param {number} titleOpinionId The id of the parent title opinion
 * @param {number} sectionId The id of the section
 * @returns {AutoSaver} 
 */
export function useAutoSave(sectionType, titleOpinionId = 0, sectionId = 0) {
    const { replace, pathname } = useRouter();

    const reload = useCallback((s) => {
        if (titleOpinionId == 0) {
            replace(pathname.replace('/0/', `/${s.titleOpinionId}/`));
        } else {
            replace(pathname);
        }
    }, [titleOpinionId]);

    const createSection = useCallback(async (sectionTypeId, path, value) => {
        const args = {
            sectionTypeId,
            titleOpinionId,
            path,
            value
        };

        return sectionApi.createSection(args);
    }, [titleOpinionId]);

    const fieldSaver = useCallback(async (value, path, id = 0, stateValues = {}) => {
        const _sectionId = sectionId > 0 ? sectionId : id;
        if (_sectionId > 0) {
            if(stateValues && !isEmpty(stateValues)) {
                for (const key of Object.keys(stateValues)) {
                    await sectionApi.updateSection({ id: _sectionId, path: key, value: stateValues[key] });
                }
            } else {
                await sectionApi.updateSection({ path, value, id: _sectionId });
            }
        } else {

            const newSection = await createSection(sectionType, path, value);

            await saveMultipleSectionFields(newSection.sectionId, stateValues);
            
            reload(newSection);
        }
    }, [sectionId, titleOpinionId, sectionType]);

    const saveDocument = useCallback(async (doc, docType, id) => {
        const _sectionId = sectionId > 0 ? sectionId : id;

        if (_sectionId > 0) {
            console.log(`File type ${docType} - ${JSON.stringify(doc)}`);

            await document.upload(doc);
            const attachment = { ...doc.uploadInfo, typeId: docType };
            await sectionApi.addSectionDocument(_sectionId, attachment);

            reload();
        }

    }, [sectionId, titleOpinionId, sectionType]);

    const deleteDocument = useCallback(async (docId, id) => {
        const _sectionId = sectionId > 0 ? sectionId : id;

        if (_sectionId > 0) {
            await sectionApi.deleteSectionDocument(_sectionId, docId);
            reload();
        }
    }, [sectionId, titleOpinionId, sectionType]);
    
    const debounced = debounce(fieldSaver, 250);

    return { 
        saveField: debounced,
        saveDocument,
        deleteDocument
    };
}

export function useSectionDetail(sectionType, sectionId = 0, titleOpinionId = 0) {
    const { replace, pathname } = useRouter();

    const reload = useCallback(s => {
        if (titleOpinionId == 0) {
            replace(pathname.replace('/0/', `/${s.titleOpinionId}/`));
        } else {
            replace(pathname);
        }
    }, [titleOpinionId]);

    const saveSectionDetail = useCallback(async (data, id = 0, stateValues = {}) => {
        const { documents, ...value } = data;

        if (sectionId == 0 && (value.sectionId == undefined || value.sectionId == 0)) {
            const newSectionArgs = {
                sectionTypeId: sectionType,
                titleOpinionId,
                path: Object.keys(stateValues)[0],
                value: Object.values(stateValues)[0]
            };
            const newSection = await sectionApi.createSection(newSectionArgs);
            value.sectionId = newSection.sectionId;

            await saveMultipleSectionFields(newSection.sectionId, stateValues);
            
            sectionApi.saveSectionDetail(value)
                .then(async (detail) => {
                    if (Array.isArray(documents) && documents.length > 0) {
                        await Promise.all(
                            documents.map(d => {
                                if (!d.uploaded)
                                    return uploadSectionDetailDocument(detail.sectionDetailId, d);
                            })
                        );
                    }
                    reload(newSection);
                });

        } else {
            const result = await sectionApi.saveSectionDetail(value, id);
            const {sectionDetailId} = result;
            if (Array.isArray(documents) && documents.length > 0) {
                await Promise.all(
                    documents.map(d => {
                        if (!d.uploaded)
                            return uploadSectionDetailDocument(sectionDetailId, d);
                        if (d.isDeleted)
                            return sectionApi.deleteSectionDetailDocument(sectionDetailId, d.sectionDetailDocumentId);
                    })
                );
            }
            replace(pathname);
        }
    }, [sectionId, titleOpinionId]);

    const deleteSectionDetail = async (id = 0) => {
        sectionApi.deleteSectionDetail(id).then(() => {
            replace(pathname);
        });        
    };  

    const deleteAllSectionDetails = async (sectionDetails) => {
        if(sectionDetails && sectionDetails.length > 0){
            await sectionDetails.map(f => sectionApi.deleteSectionDetail(f.sectionDetailId));
        }  
        replace(pathname);                  
    };  

    return {
        saveDetail: debounce(saveSectionDetail, 250),
        deleteDetail: debounce(deleteSectionDetail, 250),
        deleteAllDetails: debounce(deleteAllSectionDetails, 250)
    };
}

async function saveMultipleSectionFields(id, values) {
    if (id && !isEmpty(values)) {
        for (const key of Object.keys(values)) {
            await sectionApi.updateSection({ id, path: key, value: values[key] });
        }
    }
}

async function uploadSectionDetailDocument(sectionDetailId, doc) {
    const maxRetries = 3;
    const baseDelay = 1000;
    let lastError;
    for (let attempt = 1; attempt <= maxRetries; attempt++) {
        try {
            const { success, message } = await document.upload(doc);
            if (success) {
                lastError = null;
                break;
            }
            lastError = new Error(message || 'Unknown upload error');
            remoteLog('log', `uploadSectionDetailDocument: attempt ${attempt} failed`, { doc, message });
        } catch (err) {
            lastError = err;
            remoteLog('error', `uploadSectionDetailDocument: exception on attempt ${attempt}`, { doc, error: err?.message, stack: err?.stack });
        }
        if (attempt < maxRetries) {
            const delay = baseDelay * Math.pow(2, attempt - 1);
            await new Promise(res => setTimeout(res, delay));
        }
        if (attempt === maxRetries && lastError) {
            remoteLog('error', `uploadSectionDetailDocument: all ${maxRetries} attempts failed`, { doc, error: lastError?.message, stack: lastError?.stack });
            throw lastError;
        }
    }
    const attachment = { ...doc.uploadInfo, typeId: doc.type };
    await sectionApi.addSectionDetailDocument(sectionDetailId, attachment);
}